import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { BRAND_FULL_NAME } from '../../config/brand';
import { cardTheme } from '../../styles/cardTheme';
import { useAuth } from '../../contexts/AuthContext';

/** Subtle Islamic star outline for CTA decoration */
const StarOrnament: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden
  >
    <path
      d="M60 8 L68 40 L98 28 L80 52 L112 60 L80 68 L98 92 L68 80 L60 112 L52 80 L22 92 L40 68 L8 60 L40 52 L22 28 L52 40 Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinejoin="round"
    />
    <path
      d="M60 28 L64 48 L82 40 L70 56 L90 60 L70 64 L82 80 L64 72 L60 92 L56 72 L38 80 L50 64 L30 60 L50 56 L38 40 L56 48 Z"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinejoin="round"
      opacity="0.7"
    />
  </svg>
);

const HomeCtaBanner: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <section id="home-cta-section" className="py-2 sm:py-4">
      <div
        className={`relative overflow-hidden rounded-2xl sm:rounded-3xl ${cardTheme.headerBanner} px-5 py-8 sm:px-8 sm:py-10 lg:px-12 lg:py-12`}
      >
        <StarOrnament className="pointer-events-none absolute -end-4 bottom-0 h-36 w-36 text-gold-400/25 sm:h-48 sm:w-48 lg:-end-2 lg:bottom-[-1rem] lg:h-56 lg:w-56" />
        <StarOrnament className="pointer-events-none absolute -start-8 -top-6 h-28 w-28 text-white/10 sm:h-36 sm:w-36" />

        <div className="relative z-10 max-w-2xl">
          <h3 className="mb-3 text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-snug font-cairo-bold">
            مستعد تبدأ مذاكرتك مع {BRAND_FULL_NAME}؟
          </h3>
          <p className="mb-6 sm:mb-8 text-sm sm:text-base text-white/85 leading-relaxed">
            انضم للمنصة دلوقتي، اختار مدرسك والكورسات المناسبة لصفك، وابدأ تتعلم بالطريقة اللي تناسبك.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {mounted && !isAuthenticated ? (
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="inline-flex min-h-11 w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-white px-6 py-2.5 text-sm sm:text-base font-semibold text-green-900 shadow-sm transition hover:bg-gold-50"
              >
                سجّل مجاناً
                <ArrowLeft className="h-4 w-4" aria-hidden />
              </button>
            ) : mounted && isAuthenticated ? (
              <button
                type="button"
                onClick={() => navigate('/student-portal')}
                className="inline-flex min-h-11 w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-white px-6 py-2.5 text-sm sm:text-base font-semibold text-green-900 shadow-sm transition hover:bg-gold-50"
              >
                ادخل لمنصتك
                <ArrowLeft className="h-4 w-4" aria-hidden />
              </button>
            ) : null}
            <button
              type="button"
              onClick={() =>
                document.getElementById('suggested-courses-section')?.scrollIntoView({ behavior: 'smooth' })
              }
              className="inline-flex min-h-11 w-full sm:w-auto items-center justify-center gap-2 rounded-full border-2 border-white/80 px-6 py-2.5 text-sm sm:text-base font-semibold text-white transition hover:bg-white/10"
            >
              شوف الكورسات
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeCtaBanner;
